import {
  Page,
  Card,
  IndexTable,
  TextField,
  Filters,
  ChoiceList,
  Badge,
  Pagination,
  Text,
  useIndexResourceState,
  BlockStack,
} from '@shopify/polaris';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '../api';

interface User {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  createdAt: string;
  updatedAt: string;
  balance: number;
  currentStreak: number;
}

interface UsersResponse {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export default function Users() {
  const navigate = useNavigate();
  const [data, setData] = useState<UsersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<string[]>([]);
  const [page, setPage] = useState(1);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '50' });
      if (search) params.set('search', search);
      if (filter.length > 0) params.set('filter', filter[0]);
      const result = await apiGet<UsersResponse>(`/panel-api/users?${params}`);
      setData(result);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, search, filter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleFilterChange = useCallback((value: string[]) => {
    setFilter(value);
    setPage(1);
  }, []);

  const handleClearAll = useCallback(() => {
    setSearch('');
    setFilter([]);
    setPage(1);
  }, []);

  const users = data?.users ?? [];
  const { selectedResources, allResourcesSelected, handleSelectionChange } =
    useIndexResourceState(users as any);

  const filters = [
    {
      key: 'activity',
      label: 'Activity',
      filter: (
        <ChoiceList
          title="Activity"
          titleHidden
          choices={[
            { label: 'Active last 30 days', value: 'active_last_30d' },
            { label: 'Has contract', value: 'has_contract' },
          ]}
          selected={filter}
          onChange={handleFilterChange}
        />
      ),
      shortcut: true,
    },
  ];

  const resourceName = { singular: 'user', plural: 'users' };

  const rowMarkup = users.map((user, idx) => (
    <IndexTable.Row
      id={user.id}
      key={user.id}
      position={idx}
      selected={selectedResources.includes(user.id)}
      onClick={() => navigate(`/users/${user.id}`)}
    >
      <IndexTable.Cell>
        <Text as="span" variant="bodyMd" fontWeight="bold">
          {user.firstName ?? ''} {user.lastName ?? ''}
        </Text>
      </IndexTable.Cell>
      <IndexTable.Cell>{user.email}</IndexTable.Cell>
      <IndexTable.Cell>
        <Badge>{`${user.balance.toLocaleString()} pts`}</Badge>
      </IndexTable.Cell>
      <IndexTable.Cell>{user.currentStreak}d streak</IndexTable.Cell>
      <IndexTable.Cell>
        {new Date(user.createdAt).toLocaleDateString()}
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  return (
    <Page title="Users" subtitle={data ? `${data.pagination.total} total` : ''}>
      <Card padding="0">
        <BlockStack>
          <div style={{ padding: '12px 16px' }}>
            <Filters
              queryValue={search}
              queryPlaceholder="Search by name or email..."
              onQueryChange={handleSearchChange}
              onQueryClear={() => setSearch('')}
              onClearAll={handleClearAll}
              filters={filters}
            />
          </div>
          <IndexTable
            resourceName={resourceName}
            itemCount={users.length}
            selectedItemsCount={
              allResourcesSelected ? 'All' : selectedResources.length
            }
            onSelectionChange={handleSelectionChange}
            headings={[
              { title: 'Name' },
              { title: 'Email' },
              { title: 'Points' },
              { title: 'Streak' },
              { title: 'Joined' },
            ]}
            loading={loading}
          >
            {rowMarkup}
          </IndexTable>
        </BlockStack>
      </Card>
      {data && data.pagination.totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
          <Pagination
            hasPrevious={page > 1}
            hasNext={page < data.pagination.totalPages}
            onPrevious={() => setPage(page - 1)}
            onNext={() => setPage(page + 1)}
          />
        </div>
      )}
    </Page>
  );
}
